'use client';

import { InfoCard as InfoCardComponent } from '@/components/cards/InfoCard';
import InfoCardSkeleton from '@/components/cards/InfoCardSkeleton';
import { useDynamicLocale } from '@/hoslog-intl';
import { StoreInformation, UserFriendIcon } from '@/icons';
import { MoneyBillIcon } from '@/icons/money-bill-icon';
import { WalletIcon } from '@/icons/wallet-icon';
import { cn } from '@/lib/utils';
import type { PaymentOverviewData } from '@/services/payment/payment.types';
import { LucideIcon } from 'lucide-react';
import { useState } from 'react';
import { usePaymentOverview } from '../hooks/useOverview';
import { Analytics } from './analytics';
import { SalesBreakdown } from './sales-breakdown';

export type PaymentKPICard = {
  title: string;
  info: string;
  helperText?: string;
  icon: LucideIcon;
  titleWrap?: 'auto' | 'always' | 'never';
  action?: { label: string; link: string };
  infoClassName?: string;
  helperTextClassName?: string;
};

const formatAmount = (amount?: number) => (amount ?? 0).toLocaleString();

const getKpiCards = (t: ReturnType<typeof useDynamicLocale>['t'], overview?: PaymentOverviewData): PaymentKPICard[] => [
  {
    icon: WalletIcon,
    title: t('paymentManagement.overview.cards.hoslogBalance'),
    info: formatAmount(overview?.totalHoslogBalance),
    helperText: '円',
    titleWrap: 'never',
  },
  {
    icon: MoneyBillIcon,
    title: t('paymentManagement.overview.cards.totalReceivedAmount'),
    info: formatAmount(overview?.totalReceiveAmount),
    helperText: '円',
    titleWrap: 'always',
    action: {
      label: t('common.page.button.view'),
      link: '/payment-management/overview',
    },
  },
  {
    icon: StoreInformation,
    title: t('paymentManagement.overview.cards.totalStoreAmount'),
    info: formatAmount(overview?.totalStoreAmount),
    helperText: '円',
    titleWrap: 'always',
    action: {
      label: t('common.page.button.view'),
      link: '/payment-management/store-payment-log',
    },
  },
  {
    icon: UserFriendIcon,
    title: t('paymentManagement.overview.cards.totalAffiliateAmount'),
    info: formatAmount(overview?.totalAffiliateAmount),
    helperText: '円',
    titleWrap: 'always',
    action: {
      label: t('common.page.button.view'),
      link: '/payment-management/affiliate-log',
    },
  },
];

export const OverView = () => {
  const { t } = useDynamicLocale();
  const [activeTab, setActiveTab] = useState<'analytics' | 'sales-breakdown'>('analytics');
  const { paymentOverview, isLoading } = usePaymentOverview();
  const kpiCards = getKpiCards(t, paymentOverview);

  return (
    <div className="flex flex-col gap-6">
      {/* KPI Summary Cards Row (4-Column Grid) */}
      {isLoading ? (
        <InfoCardSkeleton count={4} />
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {kpiCards.map((card) => (
            <InfoCardComponent
              key={card.title}
              icon={card.icon}
              info={card.info}
              title={card.title}
              titleWrap={card.titleWrap}
              action={card.action}
              helperText={card.helperText}
              infoClassName="text-[48px] leading-[100%]"
              helperTextClassName={card.helperTextClassName}
            />
          ))}
        </div>
      )}

      {/* Segmented Tab Switcher */}
      <div className="w-full bg-surface-subtle p-0 overflow-hidden flex rounded-[8px] border border-contrast">
        <button
          type="button"
          onClick={() => setActiveTab('analytics')}
          className={cn(
            'flex-1 py-3 text-center text-sm font-bold transition-all cursor-pointer',
            activeTab === 'analytics'
              ? 'bg-contrast text-on-color shadow-sm'
              : 'bg-surface-subtle text-foreground-heading hover:bg-surface-hover',
          )}
        >
          {t('paymentManagement.overview.tabs.analytics')}
        </button>
        <button
          type="button"
          onClick={() => setActiveTab('sales-breakdown')}
          className={cn(
            'flex-1 py-3 text-center text-sm font-bold transition-all cursor-pointer',
            activeTab === 'sales-breakdown'
              ? 'bg-contrast text-on-color shadow-sm'
              : 'bg-surface-subtle text-foreground-heading hover:bg-surface-hover',
          )}
        >
          {t('paymentManagement.overview.tabs.salesBreakdown')}
        </button>
      </div>

      {/* Content depending on active tab */}
      <div>{activeTab === 'analytics' ? <Analytics /> : <SalesBreakdown />}</div>
    </div>
  );
};

export default OverView;
